/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DANGER,
  BUTTON_THEME_DEFAULT,
  Button,
  DialogContentProps,
  DialogProps,
  TOAST_TYPE_DANGER,
  TOAST_TYPE_SUCCESS,
  Toast,
} from '@squiz/sds';
import { DIALOG_SIZE_MD, useDialogStore, useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { CDP_STRINGS } from '../../Constants/Strings.constants';
import { getScvService } from '../../Services/single-customer-view/src/Utilities/GetScvService/GetScvService';
import { UseCustomerDeleteProps, UseCustomerDeleteResponse } from './UseCustomerDelete.hook.def';

export const useCustomerDelete = (props: UseCustomerDeleteProps): UseCustomerDeleteResponse => {
  const { onDelete } = props;
  const [dialogPropState, setDialogPropState] = useState<DialogProps | null>(null);
  const { updateActiveDialog } = useDialogStore();
  const { updateActiveToast } = useToastStore();
  const singleCustomerViewService = getScvService();
  const { t } = useTranslation();

  const labels = useMemo((): Record<string, string> => {
    return {
      cancel: t(CDP_STRINGS.cancel as string),
      delete: t(CDP_STRINGS.delete as string),
      deleteCustomer: t(CDP_STRINGS.deleteCustomer as string),
      deleteCustomerDescription: t(CDP_STRINGS.deleteCustomerDescription as string),
    };
  }, [t]);

  const openDeleteCustomerModal = useCallback(
    (customerId: string, fullName: string): void => {
      const dialogProps: DialogProps = {
        buttons: [
          <Button
            format={BUTTON_FORMAT_SOFT}
            key="cancel-delete-customer"
            onClick={() => updateActiveDialog(null)}
            text={labels.cancel}
            theme={BUTTON_THEME_DEFAULT}
          />,
          <Button
            key="confirm-delete-customer"
            onClick={(): void => {
              updateActiveDialog(null);
              lastValueFrom(
                singleCustomerViewService.deleteCustomer({
                  customerId,
                }),
              )
                .then(onDelete)
                .then((): void => {
                  updateActiveToast(
                    <Toast
                      data-id={`delete-customer-toast-success`}
                      showToast
                      stateHandler={useToastStore}
                      title={`Deleted`}
                      toastContent={
                        <>
                          <span className="delete-customer-toast-description">
                            You have successfully deleted the customer
                          </span>
                          <span className="delete-customer-toast-name"> {fullName}</span>
                        </>
                      }
                      toastType={TOAST_TYPE_SUCCESS}
                    />,
                  );
                  return;
                })
                .catch((error): void => {
                  updateActiveDialog(null);
                  // eslint-disable-next-line no-console
                  console.error(error);
                  updateActiveToast(
                    <Toast
                      data-id={`delete-customer-toast-error`}
                      showToast
                      stateHandler={useToastStore}
                      title={`Delete customer failed`}
                      toastContent={<span>{error.message}</span>}
                      toastType={TOAST_TYPE_DANGER}
                    />,
                  );
                });
            }}
            text={labels.delete}
            theme={BUTTON_THEME_DANGER}
          />,
        ],
        classes: 'pb-personalization-modal',
        dialogContent: {
          renderContent: (props: DialogContentProps): JSX.Element => <div>{labels.deleteCustomerDescription}</div>,
        },
        dialogSize: DIALOG_SIZE_MD,
        heading: labels.deleteCustomer,
        scrollOnOverflow: false,
        showDividers: false,
        stateHandler: useDialogStore,
      };
      setDialogPropState(dialogProps);
      updateActiveDialog(dialogProps);
    },
    [
      labels.cancel,
      labels.delete,
      labels.deleteCustomer,
      labels.deleteCustomerDescription,
      onDelete,
      singleCustomerViewService,
      updateActiveDialog,
      updateActiveToast,
    ],
  );

  return {
    dialogProps: dialogPropState,
    openDeleteCustomerModal,
  };
};
